import React, { useEffect, useState } from "react";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import IdentityInventoryPanel from "./IdentityInventoryPanel";
import DeviceInventoryPanel from "./DeviceInventoryPanel";
import IdentityCoverageCard from "./IdentityCoverageCard";
import IdentityMap from "./IdentityMap";
import NercSitesPanel from "./NercSitesPanel";
import { ChromeTabs, TAB_PANEL_CLASS } from "./TabStrips";
import { btn } from "../utils/buttonStyles";
import { Monitor, Network, Users, Zap } from "lucide-react";

/**
 * GRC → Assets: the objects the platform holds and computes controls from — identities first;
 * devices, AI assets, cloud apps and cloud resources attach to the same tab as their feeds land
 * (one tab, a type switcher, never a page per feed). Assets are never controls: nothing here
 * scores, and the Controls tab shows the assurance controls computed from them.
 */
export const ASSET_TYPES = [
  { key: "identity", label: "Identities", available: true, icon: Users },
  { key: "device",   label: "Devices",    available: true, icon: Monitor },
  // The same objects as the two lists beside it, drawn as a chain instead of read as rows. It
  // belongs here rather than on its own page: a map that lived elsewhere would be a second view of
  // one estate that could drift from the inventory it is drawn from.
  { key: "map",      label: "Map",        available: true, icon: Network },
];

/**
 * NERC CIP-002 inventory (point 3). Listed only when the backend says it is available for the
 * organization (Features:NercAssets on, and NERC CIP applies to the org): one rule, server side.
 */
export const NERC_ASSET_TYPE = { key: "nerc", label: "NERC sites", available: true, icon: Zap };

// The last answer per organization, so returning to Assets shows every tab at once; it is still
// asked again each time, and the fresh answer replaces it.
const nercAvailabilityByOrg = new Map();

/**
 * Whether the NERC sites type is shown: null until the first answer for this organization, then
 * true / false; any failure (flag off answers 404) hides it. The inventory and patch management are
 * org-wide data (backend CanViewAllGrcData), so a Deal-, BU- or Self-scoped user gets false at once
 * and the availability call is never made.
 */
export function useNercSitesAvailable(organizationId) {
  const apiCall = useApi();
  const { canViewAllGrcData } = useRBAC();
  const canView = !!organizationId && canViewAllGrcData(organizationId);
  const [answer, setAnswer] = useState(() => ({ org: organizationId, available: nercAvailabilityByOrg.get(organizationId) ?? null }));
  useEffect(() => {
    let live = true;
    // Functional updates that keep the same object when nothing changed, so a re-run of this effect
    // never causes a render of its own.
    if (!canView) {
      setAnswer((prev) => (prev.org === organizationId && prev.available === false ? prev : { org: organizationId, available: false }));
      return undefined;
    }
    setAnswer((prev) => (prev.org === organizationId && prev.available !== false ? prev
      : { org: organizationId, available: nercAvailabilityByOrg.get(organizationId) ?? null }));
    const settle = (available) => {
      nercAvailabilityByOrg.set(organizationId, available);
      if (live) setAnswer((prev) => (prev.org === organizationId && prev.available === available ? prev : { org: organizationId, available }));
    };
    apiCall("GET", "/nerc-assets/availability", null, false, null, true)
      .then((res) => settle(res?.available === true))
      .catch(() => settle(false));
    return () => { live = false; };
  }, [apiCall, organizationId, canView]);
  if (!canView) return false;
  // An answer for the previous organization is not this one's.
  return answer.org === organizationId ? answer.available : null;
}

export default function AssetsTab({ organizationId, initialType = "identity", initialIdentityId = null, initialDeviceId = null, initialSearch = null, onOpenControls }) {
  const nercAnswer = useNercSitesAvailable(organizationId);
  const nercAvailable = nercAnswer === true;
  const types = nercAvailable ? [...ASSET_TYPES, NERC_ASSET_TYPE] : ASSET_TYPES;
  const [type, setType] = useState(types.some(t => t.key === initialType && t.available) ? initialType : "identity");
  // A deep link to NERC sites can only be honoured once availability has loaded.
  useEffect(() => { if (initialType === NERC_ASSET_TYPE.key && nercAvailable) setType(NERC_ASSET_TYPE.key); }, [initialType, nercAvailable]);
  useEffect(() => { if (nercAnswer === false && type === NERC_ASSET_TYPE.key) setType("identity"); }, [type, nercAnswer]);

  // Every tab appears together: hold the strip (and the panels, so their requests queue behind this
  // small one) until NERC sites is known to be shown or not.
  if (nercAnswer === null) {
    return (
      <div className="max-w-7xl mx-auto px-5 pb-8 pt-4" data-testid="assets-tab">
        <div className="h-[46px] rounded-t-xl bg-gradient-to-b from-sky-100 to-sky-50 border border-gray-200 animate-pulse" aria-busy="true" aria-label="Loading asset types" />
        <div className={TAB_PANEL_CLASS}><div className="h-24" /></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 pb-8 pt-4" data-testid="assets-tab">
      <ChromeTabs ariaLabel="Asset type" value={type} onChange={setType}
        items={types.map(t => ({ ...t, disabled: !t.available }))} />

      <div className={TAB_PANEL_CLASS}>
        {type !== NERC_ASSET_TYPE.key && <div className="text-xs text-gray-500">
          Objects the controls are computed from. Assets never score — the readiness effect lives in{" "}
          {onOpenControls
            ? <button type="button" onClick={onOpenControls} className={btn("secondary", "xs")}>Controls → Identity assurance</button>
            : <span className="font-medium">Controls → Identity assurance</span>}.
        </div>}

        {type === "identity" && <IdentityCoverageCard organizationId={organizationId} />}
        {type === "identity" && <IdentityInventoryPanel organizationId={organizationId} initialIdentityId={initialIdentityId} initialSearch={initialSearch} />}
        {type === "device" && <DeviceInventoryPanel organizationId={organizationId} initialDeviceId={initialDeviceId} />}
        {type === NERC_ASSET_TYPE.key && nercAvailable && <NercSitesPanel organizationId={organizationId} />}
        {type === "map" && (
          <>
            <p className="text-xs text-gray-500">
              Identity → privilege → device → application → data reach → business service. A dot&apos;s size
              is the exposure it carries; a dashed grey dot is something Axaris could not read, with the
              reason on the right. Click a dot to isolate just that object&apos;s chain; drag any dot.
            </p>
            <IdentityMap organizationId={organizationId} height={620} />
          </>
        )}
      </div>
    </div>
  );
}
